import axios from 'axios';
import { Chunk, GetEntriesParams, GetEntriesResponse, QueryChunksParams, QueryChunksResponse, SaveSessionsRequest, SessionsResponse, Sessions, UploadResponse } from '../types';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
const STORAGE_KEY = 'google_auth_token';
const SESSION_TOKEN_KEY = 'session_auth_token';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Custom event name for auth expiration
export const AUTH_EXPIRED_EVENT = 'auth:expired';

// Add request interceptor to include the latest token
apiClient.interceptors.request.use(
  (config) => {
    // Try to get session token first, fall back to Google token
    const sessionToken = localStorage.getItem(SESSION_TOKEN_KEY);
    const googleToken = localStorage.getItem(STORAGE_KEY);

    if (sessionToken) {
      config.headers.Authorization = `Bearer ${sessionToken}`;
    } else if (googleToken) {
      config.headers.Authorization = `Bearer ${googleToken}`;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle 401 errors (expired/invalid token)
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Clear the expired tokens from localStorage
      localStorage.removeItem(SESSION_TOKEN_KEY);
      localStorage.removeItem(STORAGE_KEY);
      // Dispatch custom event to notify AuthContext
      window.dispatchEvent(new CustomEvent(AUTH_EXPIRED_EVENT));
    }
    return Promise.reject(error);
  }
);

const apiService = {
  // Authentication endpoints
  exchangeGoogleToken: async (googleToken: string) => {
    return apiClient.post<{ token: string; expires_in: number }>('/auth/google', {
      credential: googleToken,
    });
  },

  // Upload endpoints
  uploadDiary: async (content: string, delimiter?: string, userId?: string, source?: string, autoCategorize?: boolean) => {
    return apiClient.post<UploadResponse>('/save-diary-to-vector-database', {
      content,
      delimiter,
      source,
      user_id: userId,
      auto_categorize: autoCategorize ?? true
    });
  },

  // Query endpoints
  queryChunks: async (query: string, params: QueryChunksParams, sessionId?: string, userId?: string) => {
    return apiClient.post<QueryChunksResponse>('/query-chunks', {
      query,
      top_k: 150, // Request more results so filtering still leaves enough chunks
      source_filter: params.sourceFilter,
      include_done: params.includeDone,
      include_discarded: params.includeDiscarded,
      include_selected: false, // Don't include selected chunks in search results
      session_id: sessionId,
      user_id: userId,
    });
  },

  // Chunk operations
  markDone: async (recordIds: string[], userId?: string) => {
    return apiClient.post('/mark-done', { record_ids: recordIds, user_id: userId });
  },

  markDiscarded: async (recordId: string, sessionId: string, userId?: string) => {
    return apiClient.post('/mark-discarded', { record_id: recordId, session_id: sessionId, user_id: userId });
  },

  markSelected: async (recordId: string, sessionId: string, userId?: string) => {
    return apiClient.post('/mark-selected', { record_id: recordId, session_id: sessionId, user_id: userId });
  },

  unmarkSelected: async (recordIds: string[], sessionId: string, userId?: string) => {
    return apiClient.post('/unmark-selected', { record_ids: recordIds, session_id: sessionId, user_id: userId });
  },

  getSelectedChunks: async (sessionId: string, userId?: string) => {
    return apiClient.post<QueryChunksResponse>('/get-selected-chunks', { session_id: sessionId, user_id: userId });
  },

  getDiscardedChunks: async (sessionId: string, userId?: string) => {
    return apiClient.post<QueryChunksResponse>('/get-discarded-chunks', { session_id: sessionId, user_id: userId });
  },

  getLatestChunks: async (limit: number = 150, userId?: string) => {
    return apiClient.get<QueryChunksResponse>('/get-latest-chunks', {
      params: { limit, user_id: userId }
    });
  },

  getEntries: async (params: GetEntriesParams, userId?: string) => {
    return apiClient.post<GetEntriesResponse>('/get-entries', {
      limit: params.limit ?? 50,
      cursor: params.cursor ?? null,
      source_filter: params.sourceFilter,
      include_done: params.includeDone ?? false,
      include_discarded: params.includeDiscarded ?? false,
      include_selected: params.includeSelected ?? false,
      session_id: params.sessionId,
      sort_by: params.sortBy ?? 'timestamp',
      sort_order: params.sortOrder ?? 'desc',
      user_id: userId,
    });
  },

  deleteRecord: async (recordId: string, userId?: string) => {
    return apiClient.delete('/delete-record', { data: { record_id: recordId, user_id: userId } });
  },

  updateChunk: async (recordId: string, newText: string, newSource?: string, userId?: string) => {
    return apiClient.put('/update-chunk', { record_id: recordId, new_text: newText, new_source: newSource, user_id: userId });
  },

  addChunk: async (chunk: Chunk) => {
    return apiClient.post('/add-chunk', chunk);
  },

  removeChunk: async (recordId: string) => {
    return apiClient.post('/remove-chunk', { record_id: recordId });
  },

  // Session endpoints
  getSessions: async (userId?: string) => {
    return apiClient.get<SessionsResponse>('/sessions', {
      params: { user_id: userId }
    });
  },

  saveSessions: async (sessions: Sessions, userId?: string) => {
    return apiClient.post<SaveSessionsRequest>('/save-sessions',
      { sessions },
      { params: { user_id: userId } }
    );
  },

  setCurrentSession: async (userId: string, sessionId: string) => {
    return apiClient.put(`/users/${userId}/current-session`, null, {
      params: { session_id: sessionId }
    });
  },

  cleanupSession: async (sessionId: string, userId?: string) => {
    return apiClient.delete(`/sessions/${sessionId}/cleanup`, {
      params: { user_id: userId }
    });
  },

  // Health check
  healthCheck: async () => {
    return apiClient.get('/health');
  },

  // User management
  saveUser: async (userId: string, email?: string, userType?: string, maxChunks?: number) => {
    const payload: any = {};
    if (email !== undefined) payload.email = email;
    if (userType !== undefined) payload.user_type = userType;
    if (maxChunks !== undefined) payload.max_chunks = maxChunks;

    return apiClient.put(`/users/${userId}/save`, payload);
  },

  // User sources management
  getUserSources: async (userId: string) => {
    return apiClient.get<{ user_id: string; sources: string[] }>(`/users/${userId}/sources`);
  },

  addUserCategory: async (userId: string, source: string) => {
    return apiClient.post<{ user_id: string; sources: string[]; message: string }>(
      `/users/${userId}/sources`,
      null,
      { params: { source } }
    );
  },

  deleteUserCategory: async (userId: string, source: string) => {
    return apiClient.delete<{ user_id: string; sources: string[]; message: string }>(
      `/users/${userId}/sources`,
      { params: { source } }
    );
  },

  getUserLimits: async (userId: string) => {
    return apiClient.get<{
      user_id: string;
      user_type: string;
      max_chunks: number;
      current_chunks: number;
      remaining_chunks: number;
      subscription_status: string;
      subscription_end_date: string | null;
      cancel_at_period_end: boolean;
      pending_downgrade_tier: string | null;
    }>(`/users/${userId}/limits`);
  },

  // Payment endpoints
  listPrices: async () => {
    return apiClient.get<{
      prices: Array<{
        id: string;
        name: string;
        description: string;
        amount: number;
        currency: string;
        interval: string;
        user_type: string;
      }>;
    }>('/payments/prices');
  },

  createCheckoutSession: async (priceId: string, userId: string) => {
    return apiClient.post<{
      session_id: string;
      url: string;
    }>('/payments/create-checkout-session', {
      price_id: priceId,
      user_id: userId,
    });
  },

  cancelSubscription: async (userId: string) => {
    return apiClient.post<{
      message: string;
      subscription_status: string;
      subscription_end_date?: string;
    }>('/payments/cancel-subscription', {
      user_id: userId,
    });
  },

  reactivateSubscription: async (userId: string) => {
    return apiClient.post<{
      message: string;
      subscription_status: string;
      subscription_end_date?: string;
    }>('/payments/reactivate-subscription', {
      user_id: userId,
    });
  },

  updateSubscription: async (userId: string, priceId: string) => {
    return apiClient.post<{
      message: string;
      subscription_status: string;
      subscription_end_date?: string;
    }>('/payments/update-subscription', {
      user_id: userId,
      price_id: priceId,
    });
  },

  getTierLimits: async () => {
    return apiClient.get<{
      free: number;
      standard: number;
      premium: number;
    }>('/tier-limits');
  },
};

export default apiService;
